import React, { useState, useEffect } from 'react';
import { Image, Table, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import MainTable from 'components/table/MainTable';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { handleMessage } from 'utils/helper/message';
import ActionButton from 'components/table/ActionButton';
import { AddSquare, ArrowDown2, ArrowUp2, Edit2, Trash } from 'iconsax-react';
import DeleteModal from 'components/modal/DeleteModal';
import CheckboxStyled from 'components/CheckboxStyled';
import NoImage from 'components/etc/NoImage';
import { charactersApis } from 'apis/characters/charactersApis';
import CharactersCreateProfile from './CharactersCreateProfile';
import CharactersCreateAbility from './CharactersCreateAbility';
import { IAbility, IProject } from 'utils/types/storyTypes';

/**
 * * 등장인물 메인
 */

interface IProps {
  selectProject: IProject | undefined;
}

const CharactersTable = ({ selectProject }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [deleteType, setDeleteType] = useState('profile');
  const [visible, setVisible] = useState({
    createCharacter: false,
    createAbility: false,
    editAiblity: false,
    delete: false,
    name: '',
  }); // 모달 show on/off
  const [selectedData, setSelectedData] = useState<any>(null);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleFetchData = async () => {
    if (projectId) {
      try {
        const res = await charactersApis.charactersList(projectId);
        setData(res.list);
      } catch (err) {
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  // 배경 삭제
  const handleClickDeleteModal = (record, type) => {
    setDeleteType(type);
    setSelectedData(record);
    setVisible({
      ...visible,
      delete: true,
      name: record?.speaker,
    });
  };
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);
        const url =
          deleteType === 'profile'
            ? charactersApis.charactersDelete(projectId, selectedData.id)
            : charactersApis.charactersDeleteAbility(
                projectId,
                selectedData.profile_id,
                selectedData.ability_id,
              );
        await url;
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  const handleLink = (record) => {
    navigate(`${record.id}/update`, {
      state: {
        record: record,
        speaker: record.speaker,
      },
    });
  };

  // * 능력 편집!
  const handleAbilityLink = (record) => {
    console.log('record:', record);
    const ability: IAbility = record;
    navigate(`${record.profile_id}/${record.ability_id}/update`, {
      state: {
        ability,
      },
    });
  };

  const expandedRowRender = (record) => {
    return (
      <MainTable
        columns={columnsNested}
        dataSource={record.abilities}
        isPagination={false}
      />
    );
  };

  // * 엄마테이블 액션메뉴(능력 추가, 수정, 삭제)
  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <AddSquare
            size='24'
            onClick={() => {
              setSelectedData(record);
              setVisible({ ...visible, createAbility: true });
            }}
          />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              handleLink(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 3,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record, 'profile');
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const nestedTableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              handleAbilityLink(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record, 'ability');
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t('storyEpisodeID.storyTabMenuList.charactersContent.table.name'),
      dataIndex: 'speaker',
      width: '15rem',
    },
    {
      title: t('storyEpisodeID.storyTabMenuList.charactersContent.table.main'),
      dataIndex: 'is_main',
      width: '5rem',
      align: 'center',
      render: (_) => {
        return (
          <div className='flex-center'>
            <CheckboxStyled isChecked={_} />
          </div>
        );
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.charactersContent.table.standing',
      ),
      dataIndex: 'use_standing',
      width: '5rem',
      align: 'center',
      render: (_) => {
        return (
          <div className='flex-center'>
            <CheckboxStyled isChecked={_} />
          </div>
        );
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.charactersContent.table.emoticon',
      ),
      dataIndex: 'use_emoticon',
      width: '5rem',
      align: 'center',
      render: (_) => {
        return (
          <div className='flex-center'>
            <CheckboxStyled isChecked={_} />
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
    Table.EXPAND_COLUMN,
  ];

  const columnsNested = [
    {
      title: 'ID',
      dataIndex: 'ability_id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.charactersContent.nestedTable.name',
      ),
      dataIndex: 'ability_name',
      width: '15rem',
    },
    {
      title: '선물 호감도 상한값',
      dataIndex: 'gift_limit_value',
      width: '6rem',
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.charactersContent.nestedTable.max',
      ),
      dataIndex: 'max_value',
      width: '6rem',
      align: 'center',
    },
    {
      title: '주 능력 여부',
      dataIndex: 'is_main',
      width: '6rem',
      align: 'center',
      render: (_) => {
        return (
          <div className='flex-center'>
            <CheckboxStyled isChecked={_} />
          </div>
        );
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.charactersContent.nestedTable.icon',
      ),
      dataIndex: 'icon_url',
      width: '10rem',
      align: 'center',
      render: (_, record) => (
        <IDColumnWrapper>
          {_ === null || _ === '' ? (
            <NoImage height='48px' width='48px' />
          ) : (
            <Image
              src={_}
              alt={record.ability_name}
              height={48}
              width={48}
              style={{ objectFit: 'cover' }}
            />
          )}
        </IDColumnWrapper>
      ),
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [nestedTableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
  ];

  return (
    <>
      <CreateSection>
        <DefaultButton
          name='register'
          onClick={() => setVisible({ ...visible, createCharacter: true })}
        >
          {t(
            'storyEpisodeID.storyTabMenuList.charactersContent.header.button.create',
          )}
        </DefaultButton>
      </CreateSection>
      <TableWrapper>
        <MainTable
          rowClassName={(record) => {
            if (record && record.id) {
              if (expandedRows.includes(record.id)) {
                return 'active';
              }
            }
          }}
          isExpand
          rowKey='id'
          columns={columnsOrigin}
          dataSource={data}
          isPagination={false}
          expandable={{
            expandedRowRender: (record) => {
              return expandedRowRender(record);
            },
            expandIcon: ({ expanded, onExpand, record }) => {
              return expanded ? (
                <ArrowUp2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <ArrowDown2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              );
            },
            onExpandedRowsChange: (expandedRows) => {
              setExpandedRows(expandedRows);
            },
          }}
        />
      </TableWrapper>

      <CharactersCreateProfile
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        data={selectedData}
        projectId={projectId}
      />

      <CharactersCreateAbility
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        data={selectedData}
        projectId={projectId}
      />

      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.charactersContent.modal.deleteCharacter.title',
        )}
        content={t(
          'storyEpisodeID.storyTabMenuList.charactersContent.modal.deleteCharacter.content',
        )}
        label={visible.name}
        handleDelete={handleDelete}
        loading={loading}
      />

      {/* <UploadModal
        visible={visible}
        setVisible={e => {
          setVisible({ ...visible, zip: e });
        }}
        refresh={refresh}
        setRefresh={setRefresh}
        data={selectedData}
        type="live_bg"
        projectId={projectId}
      /> */}
    </>
  );
};

export default CharactersTable;

const TableWrapper = styled.div`
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-table-thead > tr > th,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: 0 10px;
  }
`;

const CreateSection = styled.div`
  display: flex;
  justify-content: end;
`;

const IDColumnWrapper = styled.div`
  display: flex;
  column-gap: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
